<template>
  <v-card class="pa-4 koneko-visibility-settings">
    <v-alert type="info" outlined dense class="mb-4">
      <strong>Shared access map.</strong> Changes sync to
      <code>api/koneko/v1/access/</code> when the budgets/Koneko API is
      reachable (Victor and others pick them up on login). This browser also
      mirrors overrides in <code>localStorage</code> for offline dev.
      <span v-if="overrideSummary"> Saved: {{ overrideSummary }}.</span>
      Use <strong>Preview</strong> on a row to test with View as.
      <span v-if="serverSyncMessage" class="d-block mt-1">{{
        serverSyncMessage
      }}</span>
    </v-alert>

    <v-row dense class="mb-3" align="center">
      <v-col cols="12" sm="6" md="4">
        <v-text-field
          v-model="memberFilter"
          label="Filter members"
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
        />
      </v-col>
      <v-col cols="12" sm="6" class="d-flex flex-wrap" style="gap: 8px">
        <v-btn small outlined color="primary" @click="reload">Reload</v-btn>
        <v-btn small outlined @click="exportOverrides">Export JSON</v-btn>
        <v-btn small outlined @click="triggerImport">Import JSON</v-btn>
        <input
          ref="importFile"
          type="file"
          accept="application/json,.json"
          class="d-none"
          @change="onImportFile"
        />
        <v-btn small outlined @click="resetAll">Reset all overrides</v-btn>
      </v-col>
    </v-row>

    <div class="visibility-matrix-wrap">
      <table class="visibility-matrix">
        <thead>
          <tr class="visibility-matrix__group-row">
            <th class="visibility-matrix__corner" rowspan="2">Member</th>
            <th
              v-for="group in capGroups"
              :key="'g-' + group.id"
              :colspan="group.items.length"
              class="visibility-matrix__group-head"
            >
              {{ group.label }}
            </th>
            <th class="visibility-matrix__actions-head" rowspan="2">Actions</th>
          </tr>
          <tr class="visibility-matrix__cap-row">
            <th
              v-for="col in matrixColumns"
              :key="'c-' + col.cap"
              class="visibility-matrix__cap-head"
              :title="col.hint"
            >
              <span class="visibility-matrix__cap-label">{{ col.label }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="memberKey in filteredRosterKeys" :key="memberKey">
            <td class="visibility-matrix__member text-no-wrap">
              {{ memberKey }}
            </td>
            <td
              v-for="col in matrixColumns"
              :key="memberKey + '-' + col.cap"
              class="visibility-matrix__cell"
            >
              <v-checkbox
                dense
                hide-details
                class="ma-0 pa-0 d-inline-flex"
                :input-value="cellValue(memberKey, col.cap)"
                @change="setCell(memberKey, col.cap, $event)"
              />
              <span
                v-if="isOverridden(memberKey, col.cap)"
                class="visibility-matrix__override-dot"
                title="Custom override (not default)"
              />
            </td>
            <td class="visibility-matrix__row-actions">
              <v-btn
                x-small
                text
                color="primary"
                class="px-1"
                @click="previewAs(memberKey)"
              >
                Preview
              </v-btn>
              <v-btn
                x-small
                text
                class="px-1"
                :disabled="!hasRowOverrides(memberKey)"
                @click="resetMember(memberKey)"
              >
                Reset
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p
      v-if="!filteredRosterKeys.length"
      class="caption grey--text text--darken-1 mt-3 mb-0"
    >
      No members match your filter.
    </p>
    <p
      v-if="importMessage"
      class="caption mt-2 mb-0"
      :class="importError ? 'error--text' : 'success--text'"
    >
      {{ importMessage }}
    </p>
  </v-card>
</template>

<script>
import {canonicalizeKonekoUsername} from "@/config/konekoAConfig";
import {
  KONEKO_CAP,
  KONEKO_CAP_LABELS,
  KONEKO_A_ACCESS_GROUPS,
  clearKonekoVisibilityOverrides,
  clearMemberKonekoVisibilityOverrides,
  countVisibilityOverrides,
  exportVisibilityOverridesJson,
  getKonekoCapabilities,
  importVisibilityOverridesJson,
  isKonekoCapabilityOverridden,
  listKonekoVisibilityRosterKeys,
  loadServerVisibilityOverrides,
  readKonekoVisibilityOverrides,
  rosterKeyForVisibilityOverrides,
  saveKonekoVisibilityOverrides,
} from "@/config/konekoVisibility";
import {
  bumpKonekoVisibilityOverrides,
  syncDashboardViewAsFromStoreGetter,
  writeStoredViewAsUsername,
} from "@/utils/dashboardViewAsSync";

const ALL_SCOPE_CAPS = [
  KONEKO_CAP.dashboardProjectSnapshot,
  KONEKO_CAP.dashboardMemberBreakdownAll,
  KONEKO_CAP.konekoMyBudget,
  KONEKO_CAP.konekoProjectSnapshot,
  KONEKO_CAP.konekoCrossComparison,
  KONEKO_CAP.konekoOrgBudgetEditor,
  KONEKO_CAP.konekoTeamPersonalBudgets,
  KONEKO_CAP.konekoMemberBreakdownAll,
  KONEKO_CAP.konekoReassignTimesheet,
  KONEKO_CAP.navKonekoAFull,
  KONEKO_CAP.navPayroll,
];

function capsToColumns(caps) {
  return caps.map((cap) => ({
    cap,
    label: (KONEKO_CAP_LABELS[cap] || cap).split(" — ").pop(),
    hint: KONEKO_CAP_LABELS[cap] || cap,
  }));
}

function groupsToColumns(groups) {
  const cols = [];
  groups.forEach((group) => {
    group.items.forEach((item) => cols.push(item));
  });
  return cols;
}

export default {
  name: "KonekoVisibilitySettingsPanel",
  props: {
    /** `koneko-a` = grouped Koneko-focused matrix; `all` = legacy flat list. */
    scope: {
      type: String,
      default: "koneko-a",
    },
  },
  data() {
    return {
      memberFilter: "",
      rosterKeys: [],
      overrides: {},
      importMessage: "",
      importError: false,
      serverSyncMessage: "",
    };
  },
  computed: {
    overrideSummary() {
      const {members, caps} = countVisibilityOverrides();
      if (!members) return "";
      return `${members} member(s), ${caps} override(s)`;
    },
    capGroups() {
      if (this.scope === "koneko-a") return KONEKO_A_ACCESS_GROUPS;
      return [
        {
          id: "all",
          label: "All capabilities",
          items: capsToColumns(ALL_SCOPE_CAPS),
        },
      ];
    },
    matrixColumns() {
      if (this.scope === "koneko-a") return groupsToColumns(this.capGroups);
      return capsToColumns(ALL_SCOPE_CAPS);
    },
    filteredRosterKeys() {
      const q = String(this.memberFilter || "")
        .trim()
        .toLowerCase();
      if (!q) return this.rosterKeys;
      return this.rosterKeys.filter((key) =>
        String(key).toLowerCase().includes(q)
      );
    },
  },
  created() {
    this.reload();
  },
  methods: {
    async reload() {
      this.rosterKeys = listKonekoVisibilityRosterKeys();
      const loaded = await loadServerVisibilityOverrides();
      this.overrides = {...readKonekoVisibilityOverrides()};
      bumpKonekoVisibilityOverrides();
      this.serverSyncMessage = loaded
        ? "Loaded from server."
        : "Server map unavailable — showing this browser copy only.";
    },
    cellValue(memberKey, cap) {
      const perUser = this.overrides[memberKey];
      if (perUser && typeof perUser[cap] === "boolean") return perUser[cap];
      return getKonekoCapabilities(memberKey)[cap];
    },
    isOverridden(memberKey, cap) {
      return isKonekoCapabilityOverridden(memberKey, cap);
    },
    hasRowOverrides(memberKey) {
      return Boolean(
        this.overrides[memberKey] &&
          Object.keys(this.overrides[memberKey]).length
      );
    },
    async setCell(memberKey, cap, checked) {
      const key = rosterKeyForVisibilityOverrides(memberKey);
      if (!key) return;
      if (!this.overrides[key]) {
        this.$set(this.overrides, key, {});
      }
      this.$set(this.overrides[key], cap, Boolean(checked));
      const remote = await saveKonekoVisibilityOverrides(this.overrides);
      this.emitSaved();
      this.serverSyncMessage = remote.ok
        ? "Synced to server — other users see changes after refresh."
        : `Saved in this browser only; server sync failed (${
            remote.detail || remote.status
          }). Shipped grants in the SPA still apply for victor/kai.`;
    },
    exportOverrides() {
      const blob = new Blob([exportVisibilityOverridesJson()], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nekohr-koneko-access-${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      this.importMessage = "Exported overrides JSON.";
      this.importError = false;
    },
    triggerImport() {
      this.importMessage = "";
      const el = this.$refs.importFile;
      if (el) el.click();
    },
    onImportFile(ev) {
      const file = ev.target && ev.target.files && ev.target.files[0];
      ev.target.value = "";
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async () => {
        const result = await importVisibilityOverridesJson(reader.result);
        if (!result.ok) {
          this.importMessage = result.error || "Import failed";
          this.importError = true;
          return;
        }
        await this.reload();
        this.emitSaved();
        this.importMessage = `Imported overrides for ${result.members} member(s).`;
        if (result.warn) {
          this.serverSyncMessage = result.warn;
        }
        this.importError = false;
      };
      reader.readAsText(file);
    },
    async resetMember(memberKey) {
      clearMemberKonekoVisibilityOverrides(memberKey);
      if (this.overrides[memberKey]) {
        this.$delete(this.overrides, memberKey);
      }
      const remote = await saveKonekoVisibilityOverrides(this.overrides);
      this.serverSyncMessage = remote.ok
        ? "Synced to server."
        : `Reset locally; server sync failed (${
            remote.detail || remote.status
          }).`;
      this.emitSaved();
    },
    async resetAll() {
      clearKonekoVisibilityOverrides();
      this.overrides = {};
      const remote = await saveKonekoVisibilityOverrides({}, {replace: true});
      this.serverSyncMessage = remote.ok
        ? "All overrides cleared on server."
        : `Cleared locally; server sync failed (${
            remote.detail || remote.status
          }).`;
      this.emitSaved();
    },
    previewAs(memberKey) {
      const self = canonicalizeKonekoUsername(
        this.$store.getters.userProfileData &&
          this.$store.getters.userProfileData.username
      );
      const other = canonicalizeKonekoUsername(memberKey);
      if (self && other && self === other) {
        writeStoredViewAsUsername("");
      } else {
        writeStoredViewAsUsername(memberKey);
      }
      syncDashboardViewAsFromStoreGetter(
        this.$store.getters.userProfileData,
        this.$store.getters.isAuthenticated
      );
      this.$router.push({name: "KonekoA"});
    },
    emitSaved() {
      bumpKonekoVisibilityOverrides();
      this.$emit("saved");
    },
  },
};
</script>

<style scoped>
.visibility-matrix-wrap {
  overflow-x: auto;
  max-width: 100%;
}

.visibility-matrix {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 13px;
}

.visibility-matrix th,
.visibility-matrix td {
  border: 1px solid rgba(0, 0, 0, 0.12);
  vertical-align: middle;
}

.visibility-matrix__corner,
.visibility-matrix__member {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
  min-width: 160px;
  padding: 8px 12px;
}

.visibility-matrix__group-head {
  text-align: center;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #f5f5f5;
  padding: 6px 8px;
}

.visibility-matrix__cap-head {
  text-align: center;
  font-weight: 500;
  font-size: 10px;
  line-height: 1.25;
  max-width: 88px;
  padding: 6px 4px;
  background: #fafafa;
  cursor: help;
}

.visibility-matrix__cap-label {
  display: block;
  word-break: break-word;
}

.visibility-matrix__cell {
  text-align: center;
  padding: 4px;
  position: relative;
}

.visibility-matrix__actions-head,
.visibility-matrix__row-actions {
  text-align: center;
  white-space: nowrap;
  padding: 4px 8px;
  min-width: 120px;
}

.visibility-matrix__override-dot {
  display: block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ff9800;
  margin: 2px auto 0;
}

code {
  font-size: 12px;
}
</style>
